import getUserId from '../getUserId';
import type {
  AppnexusSettings,
  DeviceType,
  GetAdnSettings,
  XandrKeywords,
} from '../types';
import {
  prefixExternalKeywords,
  prepareXandrKeywords,
} from '../prepareKeywords';
import {
  getCategoryMetadataValue,
  getCategoryShortPath,
} from '../../../utils/player/category';
import { getRawAsset } from '../../../utils/player/asset';
import { getJWPlayerProvider } from '../../../utils/player/player';
import { getScreenOrientation } from '../../../utils/screenOrientation';
import { getGlimrPermissionOnce } from '../glimr';
import { waitForInitialization } from '../../paywall/spid';

import { getInvCode } from './getInvCode';
import { configAb, configSvd } from './config';
import { getRequestKeywords } from './getRequestKeywords';
import { isHermesApp } from '../../../utils/device';
import { getAdPermission } from '../getAdPermission';

const DEFAULT_TIMEOUT_IN_MILLISECONDS = 4000;

const getAdnSettings: GetAdnSettings = async (
  context,
  options,
  _stream,
  defaults,
  player
) => {
  const manager = context.autoplayManager!;
  manager.setPlayer(player);
  const { site, ads } = options;

  const siteConfig =
    site === 'svd'
      ? configSvd[site as keyof typeof configSvd]
      : configAb[site as keyof typeof configAb];
  if (!siteConfig) {
    console.warn(`Player ads config for site '${site}' not found`);
  }

  getGlimrPermissionOnce({ player, config: siteConfig?.glimr });

  const asset = getRawAsset(player);
  if (!siteConfig || !asset) {
    return null;
  }

  const {
    member,
    invCodeTemplate,
    appInvCodeTemplate,
    afpTemplate,
    formats,
    autoplayMuted,
    knownSections,
    omitUnknownSection,
    defaultSection,
  } = siteConfig;
  const defaultKeywords: XandrKeywords = {
    ...defaults,
    ...siteConfig.keywords,
  };
  const deviceType = String(defaultKeywords.supplyType || '').replace(
    'web_',
    ''
  ) as DeviceType;

  const muted = player.getMute() ?? Boolean(player.getRawConfig().mute);

  const enableDynamicAspectRatio =
    options.experiments?.find((e) => e.id === 'ads-aspect-ratio')?.variant ===
      'true' && getScreenOrientation() === 'portrait';

  const prerollFormat = manager.getPrerollFormat();
  const categoryShortPath = getCategoryShortPath(asset.category);
  const cmpConsent = await getAdPermission(context.consentManager);
  if (cmpConsent === '0') {
    // User has paid and did not consent to advertising
    return null;
  }
  const notAfpInvTemplate = isHermesApp
    ? appInvCodeTemplate || invCodeTemplate
    : invCodeTemplate;

  const currentAssetAfp =
    getCategoryMetadataValue(asset.category, 'afp') === 'true';
  const useAFP = manager.getUseAfp(currentAssetAfp);
  let invTemplate = useAFP && afpTemplate ? afpTemplate : notAfpInvTemplate;
  const effectiveSection =
    ads?.section === 'other' && knownSections && categoryShortPath
      ? categoryShortPath.split('/')[0]
      : ads?.section;
  if (effectiveSection === 'studio' && !useAFP) {
    invTemplate = configAb.live_studio.invCodeTemplate!;
  }
  const isAppTemplate = invTemplate === appInvCodeTemplate;
  const pulseUserId = getUserId();
  // Wait for identity to resolve (up to 4 s) so that the se-generic-loggedin
  // keyword reflects the user's actual login state. Falls back to null (→ 'no')
  // if identity hasn't resolved by the timeout, matching the same budget used
  // for consent and token requests.
  await waitForInitialization(DEFAULT_TIMEOUT_IN_MILLISECONDS);
  const isLoggedIn = context.identityHelper?.getUserId();

  const keywords = await getRequestKeywords({
    player,
    asset,
    deviceType,
    countryCode: defaults.countryCode
      ? String(defaults.countryCode)
      : undefined,
    supplyType: String(defaultKeywords.supplyType || '') || undefined,
    dynamicAR: enableDynamicAspectRatio,
    cmpConsent,
    siteConfig,
    ads,
    isLoggedIn: Boolean(isLoggedIn),
    pulseUserId,
  });

  const adKeywords = {
    ...prefixExternalKeywords(ads?.keywords ?? {}),
    'kw_aa-sch-has_context_keyword': ads?.keywords?.['aa-sch-context_keyword']
      ? '1'
      : '0',
    'kw_aa-sch-externalid_src': Number(!!pulseUserId) + Number(!!isLoggedIn),
  };

  const settings: AppnexusSettings = {
    member,

    options: {
      autoplayMuted: autoplayMuted || (muted && prerollFormat === 'bumper'),
      vpaidcontrols: true,
    },

    adSlots: {
      schedule: {
        preroll: formats?.preroll
          ? prepareXandrKeywords({
              'inv_code': getInvCode(
                invTemplate!,
                prerollFormat,
                deviceType,
                effectiveSection,
                useAFP,
                {
                  knownSections,
                  omitUnknownSection: isAppTemplate && omitUnknownSection,
                  defaultSection,
                }
              ),
              'kw_se-generic-adformat': useAFP
                ? `afp_${prerollFormat}`
                : prerollFormat,
              'kw_se-generic-loggedin': isLoggedIn ? 'yes' : 'no',
              ...adKeywords,
            })
          : null,
        midroll: formats?.midroll
          ? prepareXandrKeywords({
              'inv_code': getInvCode(
                invTemplate!,
                'midroll',
                deviceType,
                isAppTemplate ? undefined : effectiveSection,
                false, // afp is not supported for midrolls
                {
                  knownSections,
                  omitUnknownSection: isAppTemplate && omitUnknownSection,
                  defaultSection,
                }
              ),
              'kw_se-generic-adformat': 'midroll',
              ...adKeywords,
            })
          : null,
      },
      params: keywords,
    },

    adPlacements:
      formats?.pause && getJWPlayerProvider(player) !== 'chromecast'
        ? ({
            schedule: {
              pause: {
                sizes: [
                  [1280, 720],
                  [825, 424],
                ],
                invCode: getInvCode(
                  invTemplate!,
                  'pause',
                  deviceType,
                  effectiveSection,
                  useAFP,
                  {
                    knownSections,
                    omitUnknownSection: isAppTemplate && omitUnknownSection,
                    defaultSection,
                  }
                ),
                keywords: prepareXandrKeywords({
                  'kw_se-generic-adformat': useAFP ? 'afp_pause' : 'pause',
                  'kw_se-generic-loggedin': isLoggedIn ? 'yes' : 'no',
                  ...adKeywords,
                }),
              },
            },
            params: {
              keywords,
            },
          } as AppnexusSettings['adPlacements'])
        : null,
  };

  if (pulseUserId) {
    settings.adSlots.params.external_uid = pulseUserId;

    if (settings.adPlacements) {
      settings.adPlacements.params.keywords = {
        ...settings.adPlacements.params.keywords,
        external_uid: pulseUserId,
      };
    }
  }

  return settings;
};

export default getAdnSettings;
